import "./OptionPage.css";
import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useContext,
} from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  DEFAULT_ORIENTATION_CODE,
  MANDATORY_OPTION,
  NON_MANDATORY_OPTION,
} from "@constants/OptionPage.constants.js";
import {
  fetchStyleOptionsData,
  fetchDesignOptionsData,
  fetchDependentConflictRules,
  fetchSheetLibraryData,
  fetchAllPPOCodeListData,
  fetchPDFBundleData,
  fetchTopGroupPPOListData,
  fetchSheetTopGroupLibData,
} from "@libs/apiRequest.js";
import { compareArrays } from "@libs/generalHelper.js";
import { AuthContext } from "@contexts/AuthContext";
import useIsTablet from "@hooks/useIsTablet.js";
import DesignOptionGroup from "@components/DesignOptionGroup/DesignOptionGroup";
import MandOptionGroup from "@components/MandOptionGroup/MandOptionGroup";
import RenderOptions from "@components/RenderOptions/RenderOptions";
import OptionPageLeftPanel from "@pages/OptionPage/OptionPageLeftPanel";
import OptionPageRightPanel from "@pages/OptionPage/OptionPageRightPanel";

function OptionPage({ onModelSelect }) {
  const { selectedTab, projectName, modelName } = useParams();
  const [searchParams] = useSearchParams();
  const currentModelVersion = searchParams.get("version");
  const navigate = useNavigate();
  const { currentUserRole } = useContext(AuthContext);
  const [error, setError] = useState({
    options: "",
    sheetLib: "",
    allPPOlist: "",
    pdfPathBundles: "",
    mergePDF: "",
  });
  const [isLoading, setIsLoading] = useState({
    options: true,
    sheetLib: true,
    allPPOlist: true,
    pdfPathBundles: true,
    mergePDF: false,
  });
  const isTablet = useIsTablet();
  const [isLeftPanelOpen, setIsLeftPanelOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);

  const [selectedOrientation, setSelectedOrientation] = useState(
    DEFAULT_ORIENTATION_CODE,
  ); // default orientation
  const [selectedStyle, setSelectedStyle] = useState(null); // default style
  const [selectedPPOCodes, setSelectedPPOCodes] = useState([]); // selected options' PPO codes
  const [notSelectedPPOCodes, setNotSelectedPPOCodes] = useState([]); // not-selected options' PPO codes (needs to filter out)
  const [styleOptions, setStyleOptions] = useState([]); // style options/selections (dynamically changed by excel-sheet data)
  const [designOptions, setDesignOptions] = useState([]); // designOption options/selections (dynamically changed by excel-sheet data)
  const [dependentConflictRules, setDependentConflictRules] = useState({});
  const [ppoOptions, setPPOOptions] = useState([]); // display all options' PPO Codes
  const [selected, setSelected] = useState(() => {
    const initialSelected = {};
    designOptions.forEach((group) => {
      initialSelected[group.group] = null;
    });
    return initialSelected;
  }); // selected options

  const [pdfPathBundles, setpdfPathBundles] = useState(undefined); // select the correct folder (orientation/style): ex. inside folder /Std/C => [{ name: '<pdfName>', downloadUrl: '<downloadURL>' }, {...}],
  const [selectedpdfPaths, setSelectedpdfPaths] = useState([]); // selected combination for each sheet: ex. [{ orientationSelection: 'Std', styleSelection: 'CR', sheetNumber: '01', sheetVariation: '00' }, {...}]
  const [pdfViewerArray, setpdfViewerArray] = useState([]); //pdfArray used to feed PDFViewer: ex. ["link1", "link2", ...]
  const [isViewerArrayLoading, setIsViewerArrayLoading] = useState(true); // control displaying Spinner on the right panel when either changes - 1. Orientation 2. Style 3. pdfViewerArray
  const [sheetLibrary, setSheetLibrary] = useState({}); // fetch variations for each sheet

  // Top-Group States
  const [selectedTopGroupPPOCodes, setSelectedTopGroupPPOCodes] = useState([]); // selected top-group options' PPO codes (top-group only)
  const [notSelectedTopGroupPPOCodes, setNotSelectedTopGroupPPOCodes] =
    useState([]); // not-selected top-group options' PPO codes (top-group only)
  const [topGroupPPOOptions, setTopGroupPPOOptions] = useState([]); // top-group options' PPO Codes
  const [selectedPrintAllpdfPaths, setSelectedPrintAllpdfPaths] = useState([]);
  const [printAllPDFViewerArray, setPrintAllPDFViewerArray] = useState([]); // printAllPDFViewerArray for selcted orientation, style, and top-grouped/mandatory options (ex. Grade): ex. ["link1", "link2", ...]
  const [sheetTopGroupPPOLibrary, setSheetTopGroupPPOLibrary] = useState({}); // a library to store top-group options' PPO codes that appear on each sheet
  // sheetTopGroupPPOLibrary = {
  //   "01": ["LCO 01"],
  //   "02": ["LCO 01"],
  //   "03": [],
  //   "04": ["LCO 03"],
  //   "05": ["LCO 03","LCO 04", "LCO 01"],
  //   "06": ["LCO 01", "LCO 03"],
  //   "07": ["LCO 01"],
  // }

  // PDF Viewer CurrentPDFIndex (Top-group)
  const [hasUpdatedTopGroupOptions, setHasUpdatedTopGroupOptions] =
    useState(false);
  const prevTopGroupSelectionsRef = useRef({
    orientation: selectedOrientation,
    style: selectedStyle,
    topGroupPPOCodes: selectedTopGroupPPOCodes,
    notTopGroupPPOCodes: notSelectedTopGroupPPOCodes,
  });

  // access selected-project and selected-model through localStorage, so we can directly access previous selected project and model through URL
  useEffect(() => {
    const validateProjectModel = () => {
      const storedProject = JSON.parse(
        localStorage.getItem("projectSelection"),
      );
      const storedModel = JSON.parse(localStorage.getItem("modelSelection"));

      // Ensure the stored project and model match the URL parameters
      const isValidProject = storedProject?.name === projectName;
      const isValidModel = storedModel?.name === modelName;
      const isCorrectModelVersion =
        storedModel?.selectedVersion === currentModelVersion;
      setSelectedProject(isValidProject ? storedProject : null);
      setSelectedModel(
        isValidModel && isCorrectModelVersion ? storedModel : null,
      );

      // If no valid project or model is found, redirect to home
      if (!isValidProject) navigate("/home");
      else if (!isValidModel || !isCorrectModelVersion)
        navigate(`/home/${projectName}`);
    };

    validateProjectModel();
  }, [projectName, modelName, currentModelVersion, navigate]);

  // fetch options-for-style, create sheet-variation-library
  useEffect(() => {
    const fetchStyleOptions = async () => {
      try {
        // fetch style options
        const style_data = await fetchStyleOptionsData(
          selectedTab,
          selectedModel?.id,
          selectedModel?.selectedVersion,
        );
        setStyleOptions(style_data);

        if (style_data.length > 0) {
          handleStyleSelection(style_data[0].code);
        }

        // fetch design options
        const option_data = await fetchDesignOptionsData(
          selectedTab,
          selectedModel?.id,
          selectedModel?.selectedVersion,
        );
        setDesignOptions(option_data);

        const defaultSelected = {};
        option_data.forEach((singleGroup) => {
          const stdOption = singleGroup["options"].find(
            (option) => option.code === "",
          );

          if (stdOption) {
            defaultSelected[singleGroup["group"]] = {
              ...stdOption,
              mandatory: singleGroup["mandatory"], // Include mandatory field
            };
          }
        });

        setSelected(defaultSelected);

        // fetch dependent-conflict-rules
        const dependent_conflict_rules = await fetchDependentConflictRules(
          selectedTab,
          selectedModel?.id,
          selectedModel?.selectedVersion,
        );
        setDependentConflictRules(dependent_conflict_rules);

        setError((prevState) => ({
          ...prevState,
          options: "",
        }));
      } catch (err) {
        setError((prevState) => ({
          ...prevState,
          options: err.message,
        }));
      } finally {
        setIsLoading((prevState) => ({
          ...prevState,
          options: false,
        }));
      }
    };

    const createSheetLibrary = async () => {
      try {
        const sheetLib_data = await fetchSheetLibraryData(
          selectedTab,
          selectedModel?.id,
          selectedModel?.selectedVersion,
        );
        setSheetLibrary(sheetLib_data);
        setError((prevState) => ({
          ...prevState,
          sheetLib: "",
        }));
      } catch (err) {
        setError((prevState) => ({
          ...prevState,
          sheetLib: err.message,
        }));
      } finally {
        setIsLoading((prevState) => ({
          ...prevState,
          sheetLib: false,
        }));
      }
    };

    if (selectedModel?.id) {
      fetchStyleOptions();
      createSheetLibrary();
    }
  }, [
    projectName,
    modelName,
    selectedModel?.id,
    selectedModel?.selectedVersion,
  ]);

  // sheet names for dropdown
  const sheetNames = useMemo(
    () =>
      Object.values(sheetLibrary)
        .filter((sheet) => sheet[0].sheetName !== "")
        .map((sheet) => Object.values(sheet)[0].sheetName),
    [sheetLibrary],
  );

  // set notSelectedPPO (default to be all options)
  useEffect(() => {
    const fetchAllPPOCodeList = async () => {
      try {
        // fetch allPPOOptions
        const allPPOList_data = await fetchAllPPOCodeListData(
          selectedTab,
          selectedModel?.id,
          selectedModel?.selectedVersion,
        );
        setNotSelectedPPOCodes(allPPOList_data);
        setPPOOptions(allPPOList_data);
        setError((prevState) => ({
          ...prevState,
          allPPOlist: "",
        }));
      } catch (err) {
        setError((prevState) => ({
          ...prevState,
          allPPOlist: err.message,
        }));
      } finally {
        setIsLoading((prevState) => ({
          ...prevState,
          allPPOlist: false,
        }));
      }
    };
    if (selectedModel?.id) {
      fetchAllPPOCodeList();
    }
  }, [selectedModel?.id, selectedModel?.selectedVersion]);

  // fetch pdf-bundles (all pdf) under /:orientation/:style folder
  useEffect(() => {
    const updatePDFPathBundles = async () => {
      try {
        const pdfPathBundles_data = await fetchPDFBundleData(
          selectedTab,
          selectedModel?.id,
          selectedModel?.selectedVersion,
          selectedOrientation,
          selectedStyle,
        );
        setpdfPathBundles(pdfPathBundles_data || undefined);
        setError((prevState) => ({
          ...prevState,
          pdfPathBundles: "",
        }));
      } catch (err) {
        setError((prevState) => ({
          ...prevState,
          pdfPathBundles: err.message,
        }));
      } finally {
        setIsLoading((prevState) => ({
          ...prevState,
          pdfPathBundles: false,
        }));
      }
    };

    if (selectedModel?.id && selectedOrientation && selectedStyle !== null) {
      updatePDFPathBundles();
    }
  }, [
    selectedModel?.id,
    selectedModel?.selectedVersion,
    selectedOrientation,
    selectedStyle,
  ]);

  // set selectedTopGroupPPOOptions (all top-grouped code) and default notSelectedTopGroupPPOCodes (also all top-grouped code)
  useEffect(() => {
    const fetchTopGroupPPOList = async () => {
      try {
        // fetch selectedTopGroupPPOOptions
        const TopGroupPPOList_data = await fetchTopGroupPPOListData(
          selectedTab,
          selectedModel?.id,
          selectedModel?.selectedVersion,
        );
        setTopGroupPPOOptions(TopGroupPPOList_data);
        setNotSelectedTopGroupPPOCodes(TopGroupPPOList_data);
      } catch (err) {
        setError(`Failed to fetch data: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchSheetTopGroupLib = async () => {
      try {
        // fetch sheetTopGroupPPOLib
        const sheetTopGroupPPOLib_data = await fetchSheetTopGroupLibData(
          selectedTab,
          selectedModel?.id,
          selectedModel?.selectedVersion,
        );
        setSheetTopGroupPPOLibrary(sheetTopGroupPPOLib_data);
      } catch (err) {
        setError(`Failed to fetch data: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    if (selectedModel?.id) {
      fetchTopGroupPPOList();
      fetchSheetTopGroupLib();
    }
  }, [selectedModel?.id, selectedModel?.selectedVersion]);

  // select the correct variation for each sheet/page
  useEffect(() => {
    // create print-selected PDF Paths
    const createpdfPaths = () => {
      let pdfPath = [];

      // Select the correct variation for each sheet
      Object.entries(sheetLibrary).forEach(([sheetNumber, variations]) => {
        let bestMatch = { sheetVariation: "00", matchCount: 0 }; // Default to "STD"

        // Remove variations that contain at least one must-not-have code
        // The first filter ensures variations do not contain any mustNotHaveMandatoryPPOCodes
        let filteredVariations = variations.filter(
          (variation) =>
            Array.isArray(variation.ppoCodes) &&
            !variation.ppoCodes.some((code) =>
              notSelectedPPOCodes.includes(code),
            ),
        );

        filteredVariations.forEach((variation) => {
          let matchedCount = variation.ppoCodes.filter((code) =>
            selectedPPOCodes.includes(code),
          ).length;

          // Select the variation with the highest match count
          if (matchedCount > bestMatch.matchCount) {
            bestMatch = {
              sheetVariation: variation.sheetVariation,
              matchCount: matchedCount,
            };
          }
        });

        // Add the selected variation for the sheet
        pdfPath.push({
          orientationSelection: selectedOrientation,
          styleSelection: selectedStyle,
          sheetNumber,
          sheetVariation: bestMatch.sheetVariation,
        });
      });

      setSelectedpdfPaths(pdfPath); // Set the state after all iterations
    };

    if (selectedOrientation && selectedStyle != null) {
      createpdfPaths();
    }

    // create print-all PDF Paths
    const createPrintAllpdfPaths = () => {
      let allpdfPath = [];
      // Select the correct variations for each sheet
      Object.entries(sheetLibrary).forEach(([sheetNumber, variations]) => {
        // topGroupCodesForSheet: Retrieve top-group PPO codes that are associated with the current sheet
        const topGroupCodesForSheet =
          sheetTopGroupPPOLibrary[sheetNumber] || [];

        // === CASE 1: No top-group PPO-code options for this sheet ===
        // =================================================================================================================
        // sheetTopGroupPPOLibrary[sheetNumber] is Empty means there is no top-grouped options
        // If there are no top-group PPO codes associated with the sheet, include all variations by default
        if (topGroupCodesForSheet.length === 0) {
          variations.forEach((variation) => {
            allpdfPath.push({
              orientationSelection: selectedOrientation,
              styleSelection: selectedStyle,
              sheetNumber,
              sheetVariation: variation?.sheetVariation,
            });
          });
          return; // Move on to the next sheet
        }

        // === CASE 2: Top-group PPO-code options exist for this sheet ===
        // =================================================================================================================
        // Step 1: Exclude any variation that contains a NOT-selected top-group PPO code
        let filteredVariations = variations.filter(
          (variation) =>
            Array.isArray(variation.ppoCodes) &&
            !variation.ppoCodes.some((code) =>
              notSelectedTopGroupPPOCodes.includes(code),
            ),
        );

        // Step 2: Check if at least one of the top-group PPO codes for this sheet is selected
        const hasSelectedTopGroupCode = topGroupCodesForSheet.some((code) =>
          selectedTopGroupPPOCodes.includes(code),
        );
        // sheetLibrary: {"01": ["LCO 01"]}
        // if select: ["", "LCO03", etc.] ==> hasSelectedTopGroupCode = False
        // if select: ["LCO01"] ==> hasSelectedTopGroupCode = True

        // === CASE 2a: No selected top-group PPO codes are relevant to this sheet ===
        // =================================================================================================================
        // Just include all the filtered variations
        if (!hasSelectedTopGroupCode) {
          filteredVariations.forEach((filterVariation) => {
            allpdfPath.push({
              orientationSelection: selectedOrientation,
              styleSelection: selectedStyle,
              sheetNumber,
              sheetVariation: filterVariation?.sheetVariation,
            });
          });
          return;
        }

        // === CASE 2b: At least one relevant top-group PPO code is selected ===
        // =================================================================================================================
        // Step 3: Further filter the variations to only those that include at least one of the selected top-group PPO codes
        filteredVariations = filteredVariations.filter((variation) =>
          variation.ppoCodes.some((code) =>
            selectedTopGroupPPOCodes.includes(code),
          ),
        );

        // Step 4: (if multiple codes selected) Narrow down to variations that include "ALL" of the selected top-group codes
        const selectedTopCodesForSheet = topGroupCodesForSheet.filter((code) =>
          selectedTopGroupPPOCodes.includes(code),
        );

        // If more than one top-group PPO code is selected for this sheet, include only variations that contain "ALL" of them
        if (selectedTopCodesForSheet.length > 1) {
          filteredVariations = filteredVariations.filter((variation) =>
            selectedTopCodesForSheet.every((code) =>
              variation.ppoCodes.includes(code),
            ),
          );
        }

        // Step 5: Push all matched variations for this sheet to the PDF paths list
        filteredVariations.forEach((filterVariation) => {
          allpdfPath.push({
            orientationSelection: selectedOrientation,
            styleSelection: selectedStyle,
            sheetNumber,
            sheetVariation: filterVariation?.sheetVariation,
          });
        });
      });
      setSelectedPrintAllpdfPaths(allpdfPath);
    };

    if (
      selectedOrientation &&
      selectedStyle != null &&
      Object.keys(sheetLibrary).length > 0 &&
      Object.keys(sheetTopGroupPPOLibrary).length > 0
    ) {
      createPrintAllpdfPaths();
    }
  }, [
    selectedOrientation,
    selectedStyle,
    sheetLibrary,
    selectedPPOCodes, // Ensure this is included if needed for matching
    notSelectedPPOCodes, // Ensure this is included if needed for matching
    notSelectedTopGroupPPOCodes,
    sheetTopGroupPPOLibrary,
    selectedTopGroupPPOCodes,
  ]);

  // create pdf-url array
  useEffect(() => {
    setIsViewerArrayLoading(true);

    if (!pdfPathBundles || !selectedpdfPaths || selectedpdfPaths.length === 0)
      return;

    // create pdf-url array for selected orientation & style & design-options
    const generatePDFViewerArray = () => {
      let result_PDFViewerArray = [];

      selectedpdfPaths.forEach((sheetSelection) => {
        const formattedName = `${sheetSelection.orientationSelection} - ${sheetSelection.styleSelection} - ${sheetSelection.sheetNumber}`;

        if (!Array.isArray(pdfPathBundles) || pdfPathBundles.length < 1) return;
        const matchingPdf = pdfPathBundles.find((pdfVariation) => {
          const [
            orientationCode,
            styleCode,
            sheetCode,
            sheetName,
            variationCode,
            ppoCodes,
          ] = pdfVariation.name.split(" - ");
          return (
            sheetSelection.sheetNumber === sheetCode &&
            sheetSelection.orientationSelection === orientationCode &&
            sheetSelection.styleSelection === styleCode &&
            sheetSelection.sheetVariation === variationCode &&
            pdfVariation.name.includes(formattedName)
          );
        });

        if (matchingPdf) {
          result_PDFViewerArray.push(matchingPdf?.downloadUrl);
        }
      });

      setpdfViewerArray(result_PDFViewerArray);
    };

    // create pdf-url array for selected orientation & style & top-group options
    const generateAllPDFViewerArray = () => {
      let result_AllPDFViewerArray = [];

      selectedPrintAllpdfPaths.forEach((sheetSelection) => {
        const formattedName = `${sheetSelection.orientationSelection} - ${sheetSelection.styleSelection} - ${sheetSelection.sheetNumber}`;

        if (!Array.isArray(pdfPathBundles) || pdfPathBundles.length < 1) return;
        const matchingPdf = pdfPathBundles.find((pdfVariation) => {
          const [
            orientationCode,
            styleCode,
            sheetCode,
            sheetName,
            variationCode,
            ppoCodes,
          ] = pdfVariation.name.split(" - ");
          return (
            sheetSelection.sheetNumber === sheetCode &&
            sheetSelection.orientationSelection === orientationCode &&
            sheetSelection.styleSelection === styleCode &&
            sheetSelection.sheetVariation === variationCode &&
            pdfVariation.name.includes(formattedName)
          );
        });

        if (matchingPdf) {
          result_AllPDFViewerArray.push(matchingPdf?.downloadUrl);
        }
      });

      setPrintAllPDFViewerArray(result_AllPDFViewerArray);
    };

    if (Array.isArray(pdfPathBundles) && pdfPathBundles.length === 0) {
      setPrintAllPDFViewerArray([]);
      setpdfViewerArray([]);
      setIsViewerArrayLoading(false);
      return;
    } else if (selectedpdfPaths.length > 0) {
      generatePDFViewerArray();
      generateAllPDFViewerArray();
      setIsViewerArrayLoading(false);
    }
  }, [selectedpdfPaths, pdfPathBundles, selectedPrintAllpdfPaths]); // Run when selectedpdfPaths or pdfPathBundles change

  const handleOptionChange = useCallback(
    (group, option) => {
      setSelected((prev) => ({
        ...prev,
        [group]: {
          ...option,
          mandatory: prev[group]?.mandatory, // Preserve mandatory field
        },
      }));

      setSelectedPPOCodes((prevPpoCodes) => {
        const newPpoCodes = new Set(prevPpoCodes);

        // Remove the previous code if it existed
        if (selected[group]?.code) {
          newPpoCodes.delete(selected[group].code);
        }

        // Add the new code if it's not empty
        if (option.code) {
          newPpoCodes.add(option.code);
        }

        // Update notSelectedPPOCodes (Codes that should not be picked up)
        setNotSelectedPPOCodes(() =>
          ppoOptions.filter((code) => !newPpoCodes.has(code)),
        );

        return Array.from(newPpoCodes); // Convert Set back to an array
      });

      setSelectedTopGroupPPOCodes((prevMandatoryPpoCodes) => {
        if (selected[group]?.mandatory !== MANDATORY_OPTION) {
          return prevMandatoryPpoCodes;
        }

        const newMandatoryPpoCodes = new Set(prevMandatoryPpoCodes);

        // Remove previous code if it existed
        if (selected[group]?.code) {
          newMandatoryPpoCodes.delete(selected[group].code);
        }

        // Add new code if it's not empty
        if (option.code) {
          newMandatoryPpoCodes.add(option.code);
        }

        // Update mustNotHaveMandatoryPPOCodes (Codes that should not be picked up)
        setNotSelectedTopGroupPPOCodes(() =>
          topGroupPPOOptions.filter((code) => !newMandatoryPpoCodes.has(code)),
        );

        return Array.from(newMandatoryPpoCodes);
      });
    },
    [ppoOptions, selected, topGroupPPOOptions],
  ); // Dependencies that might cause a re-creation

  const handleOrientationChange = (orientation) => {
    setSelectedOrientation(orientation);
    setIsViewerArrayLoading(true);
    setpdfPathBundles(undefined);
  };

  const handleStyleSelection = (style) => {
    setSelectedStyle(style);
    setIsViewerArrayLoading(true);
    setpdfPathBundles(undefined);
  };

  const notSelectablePPOCodes = useMemo(() => {
    const greyedOut = new Set();

    Object.entries(dependentConflictRules).forEach(([code, rule]) => {
      // 1. Dependent logic: grey out until dependency is selected
      if (rule.dependentOn && !selectedPPOCodes.includes(rule.dependentOn)) {
        greyedOut.add(code);
      }

      // 2. Conflict logic: grey out if any conflicting option is selected
      if (
        rule.conflictsWith.some((conflictCode) =>
          selectedPPOCodes.includes(conflictCode),
        )
      ) {
        greyedOut.add(code);
      }
    });

    return Array.from(greyedOut);
  }, [dependentConflictRules, selectedPPOCodes]);

  useEffect(() => {
    // Automatically deselect dependent options whose parent dependency is no longer selected
    Object.entries(dependentConflictRules).forEach(([code, rule]) => {
      if (rule.dependentOn && !selectedPPOCodes.includes(rule.dependentOn)) {
        Object.entries(selected).forEach(([group, option]) => {
          if (option.code === code) {
            const stdOption = designOptions
              .find((designOption) => designOption.group === group)
              ?.options.find((opt) => opt?.code === "");

            if (stdOption) {
              handleOptionChange(group, stdOption);
            }
          }
        });
      }
    });
  }, [
    selectedPPOCodes,
    dependentConflictRules,
    selected,
    designOptions,
    handleOptionChange,
  ]);

  const renderNonMandatoryOptions = useMemo(
    () => (
      <RenderOptions
        designOptions={designOptions}
        mandatoryValue={NON_MANDATORY_OPTION}
        Component={DesignOptionGroup}
        selected={selected}
        handleOptionChange={handleOptionChange}
        notSelectablePPOCodes={notSelectablePPOCodes}
        dependentConflictRules={dependentConflictRules}
      />
    ),
    [
      designOptions,
      selected,
      handleOptionChange,
      notSelectablePPOCodes,
      dependentConflictRules,
    ],
  );

  const renderMandatoryOptions = useMemo(
    () => (
      <RenderOptions
        designOptions={designOptions}
        mandatoryValue={MANDATORY_OPTION}
        Component={MandOptionGroup}
        selected={selected}
        handleOptionChange={handleOptionChange}
        notSelectablePPOCodes={notSelectablePPOCodes}
        dependentConflictRules={dependentConflictRules}
      />
    ),
    [
      designOptions,
      selected,
      handleOptionChange,
      notSelectablePPOCodes,
      dependentConflictRules,
    ],
  );

  // Track if Top-Group Option Changed
  useEffect(() => {
    const prev = prevTopGroupSelectionsRef.current;
    const hasOrientationChanged = prev.orientation !== selectedOrientation;
    const hasStyleChanged = prev.style !== selectedStyle;
    const hasTopGroupPPOCodesChanged = !compareArrays(
      prev.topGroupPPOCodes,
      selectedTopGroupPPOCodes,
    );
    const hasNotTopGroupPPOCodesChanged = !compareArrays(
      prev.notTopGroupPPOCodes,
      notSelectedTopGroupPPOCodes,
    );

    const hasChanged =
      hasOrientationChanged ||
      hasStyleChanged ||
      hasTopGroupPPOCodesChanged ||
      hasNotTopGroupPPOCodesChanged;

    setHasUpdatedTopGroupOptions(hasChanged);

    // Update the reference for next render
    prevTopGroupSelectionsRef.current = {
      orientation: selectedOrientation,
      style: selectedStyle,
      topGroupPPOCodes: selectedTopGroupPPOCodes,
      notTopGroupPPOCodes: notSelectedTopGroupPPOCodes,
    };
  }, [
    selectedOrientation,
    selectedStyle,
    selectedTopGroupPPOCodes,
    notSelectedTopGroupPPOCodes,
    selected,
  ]);

  useEffect(() => {
    const handleWheel = (e) => {
      if (e.ctrlKey) {
        e.preventDefault();
      }
    };

    document.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      document.removeEventListener("wheel", handleWheel);
    };
  }, []);

  // console.log(selectedModel)
  // console.log("Selected Orientation: ", selectedOrientation);
  // console.log("Selected Style: ", selectedStyle);
  // console.log("designOptions: ", designOptions);
  // console.log("styleOptions: " + styleOptions)
  // console.log("Selected Design Options: ", selected);
  // console.log("Sheet Library: ", sheetLibrary);
  // console.log("pdfPathBundels: ", pdfPathBundles);
  // console.log("Selected PPOCodes: ", selectedPPOCodes);
  // console.log("Not Selected PPOCodes: ", notSelectedPPOCodes);
  // console.log("pdfPath after selection: ", selectedpdfPaths);
  // console.log("pdfViewerArray: ", pdfViewerArray);
  // === Print-All ===
  // console.log("topGroupPPOOptions: ", topGroupPPOOptions);
  // console.log("sheetTopGroupPPOLibrary: ", sheetTopGroupPPOLibrary);
  // console.log("selectedTopGroupPPOCodes: ", selectedTopGroupPPOCodes);
  // console.log("notSelectedTopGroupPPOCodes: ", notSelectedTopGroupPPOCodes);
  // console.log("selectedPrintAllpdfPaths: ", selectedPrintAllpdfPaths);

  return (
    <div className="OptionPage__container">
      <OptionPageLeftPanel
        isTablet={isTablet}
        isLeftPanelOpen={isLeftPanelOpen}
        selectedTab={selectedTab}
        onModelSelect={onModelSelect}
        currentUserRole={currentUserRole}
        modelName={modelName}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        error={error}
        setError={setError}
        pdfViewerArray={pdfViewerArray}
        printAllPDFViewerArray={printAllPDFViewerArray}
        selectedStyle={selectedStyle}
        selectedProject={selectedProject}
        selectedModel={selectedModel}
        selectedOrientation={selectedOrientation}
        handleOrientationChange={handleOrientationChange}
        styleOptions={styleOptions}
        handleStyleSelection={handleStyleSelection}
        renderMandatoryOptions={renderMandatoryOptions}
        renderNonMandatoryOptions={renderNonMandatoryOptions}
      />

      <OptionPageRightPanel
        error={error}
        isTablet={isTablet}
        setIsLeftPanelOpen={setIsLeftPanelOpen}
        isLeftPanelOpen={isLeftPanelOpen}
        pdfViewerArray={pdfViewerArray}
        hasUpdatedTopGroupOptions={hasUpdatedTopGroupOptions}
        sheetNames={sheetNames}
        isViewerArrayLoading={isViewerArrayLoading}
      />
    </div>
  );
}

export default OptionPage;
